import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WorldMap from 'react-svg-worldmap';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import * as api from '../api';
import Header from '../components/Header';

export default function Map({ locale, onLocaleChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState([]);
  const [countryRegionMap, setCountryRegionMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getMatchesPublic();
        const data = res.data;
        const matchList = Object.values(data);

        const counts = {};
        matchList.forEach((m) => {
          const cc = (m.countryCode || '').toLowerCase();
          if (cc) counts[cc] = (counts[cc] || 0) + 1;
        });

        setMapData(Object.entries(counts).map(([country, value]) => ({ country, value })));

        // Build country → region mapping by probing known regions
        const regionMap = {};
        const knownRegions = ['5431954363121664', '5740767406456832'];
        await Promise.all(knownRegions.map(async (rid) => {
          try {
            const r = await api.getRegion(rid);
            if (r.data?.countryCode) regionMap[r.data.countryCode.toLowerCase()] = rid;
          } catch { /* ignore */ }
        }));
        setCountryRegionMap(regionMap);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleCountryClick = (countryCode) => {
    if (!countryCode) return;
    const cc = countryCode.toLowerCase();
    const regionId = countryRegionMap[cc];
    if (regionId) {
      navigate(`/regions/${regionId}`);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Header locale={locale} onLocaleChange={onLocaleChange} />
      <Box sx={{ pt: '80px', px: 2, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 2 }}>{t('home.worldMap', 'Match Map')}</Typography>

        {loading && <CircularProgress sx={{ mt: 4 }} />}

        {!loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <WorldMap
              color="#1976d2"
              valueSuffix="matches"
              size="responsive"
              data={mapData}
              onClickFunction={({ countryCode }) => handleCountryClick(countryCode)}
              styleFunction={({ countryValue, countryCode, minValue, maxValue }) => ({
                fill: countryValue
                  ? countryRegionMap[countryCode.toLowerCase()] ? '#1976d2' : '#90caf9'
                  : '#e0e0e0',
                stroke: '#fff',
                strokeWidth: 0.5,
                cursor: countryValue && countryRegionMap[countryCode.toLowerCase()] ? 'pointer' : 'default',
                opacity: countryValue
                  ? 0.4 + 0.6 * ((countryValue - minValue) / (Math.max(maxValue - minValue, 1)))
                  : 1,
              })}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
